import type { ContextMenuItem, PreviewSessionSnapshot } from "@afar-research/contracts";
import { getTerminalLabel } from "@afar-research/shared/terminalLabels";
import {
  Bot,
  ChevronDown,
  FileDiff,
  Files,
  Globe2,
  Fingerprint,
  GitPullRequest,
  Monitor,
  ListTodo,
  ChartNoAxesCombined,
  type LucideIcon,
  Plus,
  TerminalSquare,
  Workflow,
  X,
} from "lucide-react";
import {
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { isElectron } from "~/env";
import type { RightPanelSurface } from "~/rightPanelStore";
import { cn } from "~/lib/utils";
import { useProjects, useThreadShells } from "~/state/entities";
import { resolvePullRequestState } from "./pullRequest/pullRequestPresentation";
import { resolvePullRequestTabLink } from "./rightPanelPullRequestTab.logic";
import { projectPullRequestRepository } from "@afar-research/afar";
import { readLocalApi } from "~/localApi";
import { Tooltip, TooltipPopup, TooltipTrigger } from "~/components/ui/tooltip";
import {
  Menu,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
} from "~/components/ui/menu";
import { ScrollArea } from "~/components/ui/scroll-area";
import { faviconUrlForOrigin } from "~/lib/favicon";
import { useTheme } from "~/hooks/useTheme";
import { COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS } from "~/workspaceTitlebar";

import { PreviewPanelShell, type PreviewPanelMode } from "./preview/PreviewPanelShell";
import { PierreEntryIcon } from "./chat/PierreEntryIcon";

interface FeatureSurfaceAction {
  readonly label: string;
  readonly description: string;
  readonly onClick: () => void;
}

interface RightPanelTabsProps {
  mode: PreviewPanelMode;
  maximized?: boolean;
  maxPanelWidth?: number;
  layoutControls?: ReactNode;
  surfaces: readonly RightPanelSurface[];
  activeSurfaceId: string | null;
  pendingSurfaceIds: ReadonlySet<string>;
  previewSessions: Readonly<Record<string, PreviewSessionSnapshot>>;
  terminalLabelsById: ReadonlyMap<string, string>;
  agentLabelsById?: ReadonlyMap<string, string>;
  onActivate: (surface: RightPanelSurface) => void;
  onCloseSurface: (surface: RightPanelSurface) => void;
  onCloseOtherSurfaces: (surface: RightPanelSurface) => void;
  onCloseSurfacesToRight: (surface: RightPanelSurface) => void;
  onCloseAllSurfaces: () => void;
  onCopyFilePath: (relativePath: string) => void;
  onAddBrowser: () => void;
  /** Opens a browser tab in a named profile rather than the configured default. */
  onAddBrowserInProfile: (profileId: string) => void;
  /** Profiles offered beside "Browser". Incognito is excluded upstream. */
  browserProfiles: ReadonlyArray<{ readonly id: string; readonly name: string }>;
  onAddTerminal: () => void;
  onAddDiff: () => void;
  onAddFiles: () => void;
  onAddPullRequest?: (() => void) | undefined;
  onAddContributions?: (() => void) | undefined;
  /** Present only for a Cloud Workspace, whose Cloud Display can be watched. */
  onAddScreen?: (() => void) | undefined;
  shortcutLabels?: Readonly<Record<string, string | undefined>> | undefined;
  browserAvailable: boolean;
  diffAvailable: boolean;
  filesAvailable: boolean;
  experimentSurfaceActions?: ReadonlyArray<FeatureSurfaceAction>;
  taskCounts?: { readonly completed: number; readonly total: number };
  children: ReactNode;
}

const SURFACE_DISABLED_REASONS = {
  browser: "This Environment has no browser. Update it to a release that includes one.",
  files: "Files are only available when a project is open.",
  diff: "Diff is only available for server threads in Git repositories.",
} as const;

type TabContextMenuAction = "copy-path" | "close" | "close-others" | "close-to-right" | "close-all";

function DisabledReasonTooltip(props: { reason: string; trigger: ReactElement }) {
  return (
    <Tooltip>
      <TooltipTrigger render={props.trigger} />
      <TooltipPopup side="top">{props.reason}</TooltipPopup>
    </Tooltip>
  );
}

function SurfaceMenuItem(props: {
  available: boolean;
  disabledReason?: string;
  onClick: () => void;
  children: ReactNode;
}) {
  const item = (
    <MenuItem
      className={!props.available ? "data-disabled:pointer-events-auto" : undefined}
      onClick={props.onClick}
      disabled={!props.available}
    >
      {props.children}
    </MenuItem>
  );
  if (props.available || !props.disabledReason) return item;
  return <DisabledReasonTooltip reason={props.disabledReason} trigger={item} />;
}

type FeatureSurfaceActionCard = {
  readonly label: string;
  readonly description: string;
  readonly onClick: () => void;
  readonly icon: LucideIcon;
  readonly available: boolean;
  readonly disabledReason: string | null;
};

function RightPanelEmptyState(props: {
  onAddBrowser: () => void;
  onAddBrowserInProfile: (profileId: string) => void;
  browserProfiles: ReadonlyArray<{ readonly id: string; readonly name: string }>;
  onAddTerminal: () => void;
  onAddDiff: () => void;
  onAddFiles: () => void;
  onAddPullRequest?: (() => void) | undefined;
  onAddContributions?: (() => void) | undefined;
  onAddScreen?: (() => void) | undefined;
  shortcutLabels?: Readonly<Record<string, string | undefined>> | undefined;
  browserAvailable: boolean;
  diffAvailable: boolean;
  filesAvailable: boolean;
  experimentSurfaceActions?: RightPanelTabsProps["experimentSurfaceActions"];
}) {
  const workspaceActions = [
    {
      label: "Browser",
      description: "Open a local app or URL.",
      icon: Globe2,
      available: props.browserAvailable,
      disabledReason: SURFACE_DISABLED_REASONS.browser,
      onClick: props.onAddBrowser,
    },
    {
      label: "Terminal",
      description: "Start a shell in this workspace.",
      icon: TerminalSquare,
      available: true,
      disabledReason: null,
      onClick: props.onAddTerminal,
    },
    {
      label: "Files",
      description: "Browse and read workspace files.",
      icon: Files,
      available: props.filesAvailable,
      disabledReason: SURFACE_DISABLED_REASONS.files,
      onClick: props.onAddFiles,
    },
    {
      label: "Diff",
      description: "Review changes in this thread.",
      icon: FileDiff,
      available: props.diffAvailable,
      disabledReason: SURFACE_DISABLED_REASONS.diff,
      onClick: props.onAddDiff,
    },
    ...(props.onAddScreen
      ? [
          {
            label: "Screen",
            description: "Watch and take over the Cloud Display.",
            icon: Monitor,
            available: true,
            disabledReason: null,
            onClick: props.onAddScreen,
          },
        ]
      : []),
    {
      label: "Pull requests",
      description: "Link and review this thread's pull requests.",
      icon: GitPullRequest,
      available: Boolean(props.onAddPullRequest),
      disabledReason: "Open a thread to link pull requests.",
      onClick: () => props.onAddPullRequest?.(),
    },
    {
      label: "Contributions",
      description: "See which code agents wrote and which you did.",
      icon: Fingerprint,
      available: Boolean(props.onAddContributions),
      disabledReason: "Open a thread in a Git repository to see contributions.",
      onClick: () => props.onAddContributions?.(),
    },
  ] as const;
  const experimentActions: FeatureSurfaceActionCard[] = (props.experimentSurfaceActions ?? []).map(
    (action) => ({
      ...action,
      icon: ChartNoAxesCombined,
      available: true,
      disabledReason: null,
    }),
  );
  const hasFeatureGroups = experimentActions.length > 0;

  function actionCards(
    actions: ReadonlyArray<(typeof workspaceActions)[number] | FeatureSurfaceActionCard>,
  ) {
    return actions.map((action) => {
      const Icon = action.icon;
      const content = (
        <>
          <span className="flex w-full items-center gap-2">
            <Icon className="size-4 shrink-0" />
            <span className="text-sm font-medium">{action.label}</span>
            {props.shortcutLabels?.[action.label] ? (
              <kbd className="ml-auto rounded bg-muted px-1 text-[10px] text-muted-foreground">
                {props.shortcutLabels[action.label]}
              </kbd>
            ) : null}
          </span>
          <span className="mt-1 text-xs leading-relaxed text-muted-foreground">
            {action.description}
          </span>
        </>
      );
      if (action.available) {
        // The press is acknowledged on the spot. Everything behind it — client
        // settings, the open RPC, the desktop tab — lands later, and until then
        // an unchanged card reads as a press that did nothing.
        const card = (
          <button
            type="button"
            onClick={action.onClick}
            className="flex min-h-20 w-full flex-col items-start rounded-lg border border-border/80 bg-card/40 p-3 text-left transition hover:border-border hover:bg-accent/60"
          >
            {content}
          </button>
        );
        // The card opens the default profile, the chevron picks another. The
        // trigger is a sibling rather than a child: a button inside a button is
        // invalid, and the click would reach both.
        if (action.label !== "Browser" || props.browserProfiles.length <= 1) {
          return <div key={action.label}>{card}</div>;
        }
        return (
          <div key={action.label} className="relative">
            {card}
            <Menu>
              <MenuTrigger
                className="absolute top-2 right-2 inline-flex size-6 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                aria-label="Open browser in a profile"
              >
                <ChevronDown className="size-3.5" />
              </MenuTrigger>
              <MenuPopup align="end" side="bottom" sideOffset={6} className="min-w-40 max-w-56">
                {props.browserProfiles.map((profile) => (
                  <MenuItem
                    key={profile.id}
                    onClick={() => props.onAddBrowserInProfile(profile.id)}
                  >
                    <span className="truncate">{profile.name}</span>
                  </MenuItem>
                ))}
              </MenuPopup>
            </Menu>
          </div>
        );
      }
      const disabledCard = (
        <button
          type="button"
          className="flex min-h-20 w-full cursor-not-allowed flex-col items-start rounded-lg border border-border/80 bg-card/40 p-3 text-left opacity-40"
          aria-disabled="true"
        >
          {content}
        </button>
      );
      return (
        <DisabledReasonTooltip
          key={action.label}
          reason={action.disabledReason ?? "Unavailable"}
          trigger={disabledCard}
        />
      );
    });
  }

  return (
    <div className="flex min-h-0 flex-1 @container/launcher items-center justify-center overflow-y-auto p-5">
      <div className="w-full max-w-lg">
        <div className="mb-5 text-center">
          <h3 className="text-sm font-medium text-foreground">Open a surface</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Choose what to show in the right panel.
          </p>
        </div>
        {hasFeatureGroups ? (
          <div className="space-y-5">
            {experimentActions.length > 0 ? (
              <section>
                <p className="mb-2 text-xs font-medium text-muted-foreground">Experiment</p>
                <div className="grid grid-cols-1 @min-[20rem]/launcher:grid-cols-2 gap-2">
                  {actionCards(experimentActions)}
                </div>
              </section>
            ) : null}
            <section>
              <p className="mb-2 text-xs font-medium text-muted-foreground">Workspace</p>
              <div className="grid grid-cols-1 @min-[20rem]/launcher:grid-cols-2 gap-2">
                {actionCards(workspaceActions)}
              </div>
            </section>
          </div>
        ) : (
          <div className="grid grid-cols-1 @min-[20rem]/launcher:grid-cols-2 gap-2">
            {actionCards(workspaceActions)}
          </div>
        )}
      </div>
    </div>
  );
}

function surfaceTitle(
  surface: RightPanelSurface,
  sessions: Readonly<Record<string, PreviewSessionSnapshot>>,
  terminalLabelsById: ReadonlyMap<string, string>,
  agentLabelsById: ReadonlyMap<string, string> | undefined,
): string {
  switch (surface.kind) {
    case "diff":
      return "Diff";
    case "files":
      return "Files";
    case "file":
      return surface.relativePath.slice(surface.relativePath.lastIndexOf("/") + 1);
    case "terminal":
      return (
        terminalLabelsById.get(surface.activeTerminalId) ??
        getTerminalLabel(surface.activeTerminalId)
      );
    case "experiment":
      return surface.experimentId ?? "Experiments";
    case "screen":
      return "Screen";
    case "pull-requests":
      return "Pull requests";
    case "contributions":
      return "Contributions";
    case "pull-request":
      return `#${surface.number}`;
    case "tasks":
      return "Tasks";
    case "agent":
      return agentLabelsById?.get(surface.agentId) ?? surface.agentId;
    case "workflow":
      return surface.workflowName;
    case "preview": {
      const snapshot = surface.resourceId ? sessions[surface.resourceId] : null;
      if (!snapshot || snapshot.navStatus._tag === "Idle") return "Browser";
      if (snapshot.navStatus.title.trim().length > 0) return snapshot.navStatus.title;
      try {
        return new URL(snapshot.navStatus.url).host || "Browser";
      } catch {
        return "Browser";
      }
    }
  }
}

function PreviewFavicon({ url }: { url: string | null }) {
  const faviconUrl = faviconUrlForOrigin(url, 32);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  if (!faviconUrl || failedUrl === faviconUrl) return <Globe2 className="size-3.5 shrink-0" />;
  return (
    <img
      src={faviconUrl}
      alt=""
      aria-hidden
      draggable={false}
      className="size-3.5 shrink-0 rounded-sm"
      onError={() => setFailedUrl(faviconUrl)}
    />
  );
}

/**
 * A pull request tab wears the state the Environment last read, taken from whichever thread
 * links it, so the tab strip says open, merged or closed without a request of its own. A pull
 * request no thread links, or one not read yet, keeps the plain glyph.
 */
function PullRequestSurfaceIcon({
  surface,
}: {
  surface: Extract<RightPanelSurface, { kind: "pull-request" }>;
}) {
  const threads = useThreadShells();
  const projects = useProjects();
  const project = projects.find((entry) => entry.id === surface.projectId);
  const host =
    surface.host ?? projectPullRequestRepository(project?.repositoryIdentity)?.host ?? null;
  const environmentId = project?.environmentId ?? null;
  const observation = resolvePullRequestTabLink(threads, environmentId, host, surface)?.observation;
  if (observation == null) return <GitPullRequest className="size-3.5 shrink-0" />;
  const presentation = resolvePullRequestState({
    state: observation.state,
    isDraft: observation.isDraft,
  });
  return (
    <presentation.Icon
      aria-hidden
      className={cn("size-3.5 shrink-0", presentation.toneClassName)}
    />
  );
}

function SurfaceIcon({
  surface,
  sessions,
  theme,
}: {
  surface: RightPanelSurface;
  sessions: Readonly<Record<string, PreviewSessionSnapshot>>;
  theme: "light" | "dark";
}) {
  switch (surface.kind) {
    case "preview": {
      const snapshot = surface.resourceId ? sessions[surface.resourceId] : null;
      const url = !snapshot || snapshot.navStatus._tag === "Idle" ? null : snapshot.navStatus.url;
      return <PreviewFavicon url={url} />;
    }
    case "diff":
      return <FileDiff className="size-3.5 shrink-0" />;
    case "files":
      return <Files className="size-3.5 shrink-0" />;
    case "file":
      return (
        <PierreEntryIcon
          pathValue={surface.relativePath}
          kind="file"
          theme={theme}
          className="size-3.5"
        />
      );
    case "terminal":
      return <TerminalSquare className="size-3.5 shrink-0" />;
    case "experiment":
      return <ChartNoAxesCombined className="size-3.5 shrink-0" />;
    case "screen":
      return <Monitor className="size-3.5 shrink-0" />;
    case "pull-requests":
      return <GitPullRequest className="size-3.5 shrink-0" />;
    case "contributions":
      return <Fingerprint className="size-3.5 shrink-0" />;
    case "pull-request":
      return <PullRequestSurfaceIcon surface={surface} />;
    case "tasks":
      return <ListTodo className="size-3.5 shrink-0" />;
    case "agent":
      return <Bot className="size-3.5 shrink-0" />;
    case "workflow":
      return <Workflow className="size-3.5 shrink-0" />;
  }
}

export function RightPanelTabs(props: RightPanelTabsProps) {
  const ownsDesktopTitleBar = isElectron && props.mode === "inline";
  const { resolvedTheme } = useTheme();
  const tabListRef = useRef<HTMLDivElement>(null);

  const handleTabContextMenu = useCallback(
    async (event: ReactMouseEvent, surface: RightPanelSurface) => {
      event.preventDefault();
      event.stopPropagation();

      const api = readLocalApi();
      if (!api) return;

      const surfaceIndex = props.surfaces.findIndex((entry) => entry.id === surface.id);
      if (surfaceIndex < 0) return;

      const items: ContextMenuItem<TabContextMenuAction>[] = [];
      if (surface.kind === "file") {
        items.push({ id: "copy-path", label: "Copy path" });
      }
      items.push(
        { id: "close", label: "Close" },
        {
          id: "close-others",
          label: "Close others",
          disabled: props.surfaces.length <= 1,
        },
        {
          id: "close-to-right",
          label: "Close to the right",
          disabled: surfaceIndex >= props.surfaces.length - 1,
        },
        {
          id: "close-all",
          label: "Close all",
          disabled: props.surfaces.length === 0,
        },
      );

      const action = await api.contextMenu.show(items, { x: event.clientX, y: event.clientY });
      switch (action) {
        case "copy-path":
          if (surface.kind === "file") props.onCopyFilePath(surface.relativePath);
          break;
        case "close":
          props.onCloseSurface(surface);
          break;
        case "close-others":
          props.onCloseOtherSurfaces(surface);
          break;
        case "close-to-right":
          props.onCloseSurfacesToRight(surface);
          break;
        case "close-all":
          props.onCloseAllSurfaces();
          break;
        case null:
          break;
      }
    },
    [props],
  );

  useEffect(() => {
    const activeTab = tabListRef.current?.querySelector<HTMLElement>("[data-active-tab='true']");
    activeTab?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [props.activeSurfaceId]);

  return (
    <PreviewPanelShell
      mode={props.mode}
      maxPanelWidth={props.maxPanelWidth}
      {...(props.maximized !== undefined ? { maximized: props.maximized } : {})}
    >
      <div
        className={cn(
          "workspace-topbar gap-1 pl-2",
          props.mode === "inline" ? "pr-28" : "pr-3",
          ownsDesktopTitleBar && "wco:pr-[calc(var(--workspace-native-controls-inset)+6rem)]",
          props.mode === "inline" && props.maximized && COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS,
        )}
        data-right-panel-tabbar
      >
        <ScrollArea
          ref={tabListRef}
          hideScrollbars
          scrollFade
          className={cn("min-w-0 flex-1 rounded-none", ownsDesktopTitleBar && "drag-region")}
          data-right-panel-tab-list
        >
          <div className="flex h-full w-max min-w-full items-center gap-1">
            {props.surfaces.map((surface) => {
              const active = surface.id === props.activeSurfaceId;
              const pending = props.pendingSurfaceIds.has(surface.id);
              const title = surfaceTitle(
                surface,
                props.previewSessions,
                props.terminalLabelsById,
                props.agentLabelsById,
              );
              return (
                <div
                  key={surface.id}
                  data-active-tab={active}
                  onContextMenu={(event) => void handleTabContextMenu(event, surface)}
                  className={cn(
                    "group flex h-7 min-w-25 max-w-44 shrink-0 items-center gap-1.5 rounded-md px-2 text-sm",
                    active
                      ? "bg-accent text-foreground"
                      : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
                  )}
                >
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <button
                          type="button"
                          className="flex min-w-0 flex-1 items-center gap-1.5"
                          onClick={() => props.onActivate(surface)}
                        >
                          <SurfaceIcon
                            surface={surface}
                            sessions={props.previewSessions}
                            theme={resolvedTheme}
                          />
                          <span className="truncate">{title}</span>
                          {surface.kind === "tasks" && props.taskCounts ? (
                            <span
                              className="rounded-full bg-muted px-1.5 py-0.5 text-[9px] leading-none tabular-nums text-muted-foreground"
                              aria-label={`${props.taskCounts.completed} of ${props.taskCounts.total} tasks completed`}
                            >
                              {props.taskCounts.completed}/{props.taskCounts.total}
                            </span>
                          ) : null}
                        </button>
                      }
                    />
                    <TooltipPopup>{title}</TooltipPopup>
                  </Tooltip>
                  <button
                    type="button"
                    className={cn(
                      "relative flex size-4 shrink-0 items-center justify-center rounded hover:bg-muted focus:opacity-100",
                      pending ? "opacity-100" : "opacity-0 group-hover:opacity-100",
                    )}
                    aria-label={`Close ${title}`}
                    onClick={() => props.onCloseSurface(surface)}
                  >
                    {pending ? (
                      <>
                        <span
                          className="size-2 rounded-full bg-current group-hover:hidden"
                          aria-hidden
                        />
                        <X className="hidden size-3 group-hover:block" />
                      </>
                    ) : (
                      <X className="size-3" />
                    )}
                  </button>
                </div>
              );
            })}
            {props.surfaces.length > 0 ? (
              <Menu>
                <MenuTrigger
                  className="relative inline-flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                  aria-label="Add panel surface"
                >
                  <Plus className="size-4" />
                </MenuTrigger>
                <MenuPopup align="start" side="bottom" sideOffset={6} className="min-w-44">
                  {props.experimentSurfaceActions?.length ? (
                    <>
                      <MenuGroup>
                        <MenuGroupLabel>Experiment</MenuGroupLabel>
                        {props.experimentSurfaceActions.map((action) => (
                          <SurfaceMenuItem available key={action.label} onClick={action.onClick}>
                            <ChartNoAxesCombined />
                            {action.label}
                          </SurfaceMenuItem>
                        ))}
                      </MenuGroup>
                      <MenuSeparator />
                    </>
                  ) : null}
                  <MenuGroup>
                    {props.experimentSurfaceActions?.length ? (
                      <MenuGroupLabel>Workspace</MenuGroupLabel>
                    ) : null}
                    <SurfaceMenuItem
                      available={props.browserAvailable}
                      disabledReason={SURFACE_DISABLED_REASONS.browser}
                      onClick={props.onAddBrowser}
                    >
                      <Globe2 />
                      Browser
                    </SurfaceMenuItem>
                    {/*
                      "Browser" opens the default profile; the group below names
                      the others. Only worth listing once there is something to
                      choose between.
                    */}
                    {props.browserAvailable && props.browserProfiles.length > 1
                      ? props.browserProfiles.map((profile) => (
                          <SurfaceMenuItem
                            available
                            key={profile.id}
                            onClick={() => props.onAddBrowserInProfile(profile.id)}
                          >
                            <Globe2 className="opacity-0" />
                            <span className="truncate">{profile.name}</span>
                          </SurfaceMenuItem>
                        ))
                      : null}
                    <SurfaceMenuItem available onClick={props.onAddTerminal}>
                      <TerminalSquare />
                      Terminal
                    </SurfaceMenuItem>
                    <SurfaceMenuItem
                      available={props.filesAvailable}
                      disabledReason={SURFACE_DISABLED_REASONS.files}
                      onClick={props.onAddFiles}
                    >
                      <Files />
                      Files
                    </SurfaceMenuItem>
                    <SurfaceMenuItem
                      available={props.diffAvailable}
                      disabledReason={SURFACE_DISABLED_REASONS.diff}
                      onClick={props.onAddDiff}
                    >
                      <FileDiff />
                      Diff
                    </SurfaceMenuItem>
                    {props.onAddScreen ? (
                      <MenuItem onClick={props.onAddScreen}>
                        <Monitor className="size-4" />
                        Screen
                      </MenuItem>
                    ) : null}
                    {props.onAddPullRequest ? (
                      <MenuItem onClick={props.onAddPullRequest}>
                        <GitPullRequest className="size-4" />
                        Pull request
                      </MenuItem>
                    ) : null}
                    {props.onAddContributions ? (
                      <MenuItem onClick={props.onAddContributions}>
                        <Fingerprint className="size-4" />
                        Contributions
                      </MenuItem>
                    ) : null}
                  </MenuGroup>
                </MenuPopup>
              </Menu>
            ) : null}
          </div>
        </ScrollArea>
        {props.layoutControls}
      </div>
      <div className="flex min-h-0 flex-1 flex-col">
        {props.activeSurfaceId === null ? (
          <RightPanelEmptyState
            onAddBrowser={props.onAddBrowser}
            onAddBrowserInProfile={props.onAddBrowserInProfile}
            browserProfiles={props.browserProfiles}
            onAddTerminal={props.onAddTerminal}
            onAddDiff={props.onAddDiff}
            onAddFiles={props.onAddFiles}
            onAddPullRequest={props.onAddPullRequest}
            onAddContributions={props.onAddContributions}
            onAddScreen={props.onAddScreen}
            shortcutLabels={props.shortcutLabels}
            browserAvailable={props.browserAvailable}
            diffAvailable={props.diffAvailable}
            filesAvailable={props.filesAvailable}
            experimentSurfaceActions={props.experimentSurfaceActions}
          />
        ) : (
          props.children
        )}
      </div>
    </PreviewPanelShell>
  );
}
